"use client"

import * as React from "react"
import dagre from "@dagrejs/dagre"
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import {
  Bot,
  Check,
  Clock,
  Database,
  Flag,
  Hourglass,
  MessageSquare,
  Minus,
  Repeat,
  Upload,
  X,
  Zap,
} from "lucide-react"

import type { Schedule, Step, WorkflowSpec } from "@/lib/api"
import { SKILL_LABEL, stepDetail } from "@/lib/workflow-summary"

export type StepStates = Record<
  string,
  "running" | "succeeded" | "failed" | "skipped"
>

/**
 * Workflow rendered as a node graph, laid out with dagre.
 *
 * Hand-positioning by depth produced a diagonal cascade that ran off screen
 * once a loop nested. dagre does proper hierarchical layout — even ranks,
 * centred parents, no overlap — which is what makes n8n-style graphs readable.
 *
 * Unknown step kinds still render (as a generic node) rather than vanishing,
 * so a plan is never silently mis-drawn.
 */

const NODE_W = 268
const NODE_H = 76

const WORKER_ICON: Record<string, typeof Bot> = {
  "ai.claude": Bot,
  "fsp.app_user": Upload,
  "fsp.read": Database,
  slack: MessageSquare,
}

type Variant = "trigger" | "skill" | "loop" | "wait" | "await" | "unknown"

type NodeData = {
  label: string
  sub?: string
  badge?: string
  variant: Variant
  skillId?: string
  /** Live execution state, when a run is being watched. */
  state?: "running" | "succeeded" | "failed" | "skipped"
  /** e.g. "3 / 5" for a loop mid-flight. */
  progress?: string
}

/**
 * Base tone per kind.
 *
 * Deliberately quiet: when every node is tinted, nothing stands out, and the
 * colours that matter — a running step, a failed one — have nothing to
 * contrast against. Only the trigger and the two "this pauses" kinds carry
 * colour; ordinary steps are plain cards.
 */
const TONE: Record<Variant, string> = {
  trigger: "border-primary/40 bg-primary/[0.07]",
  loop: "border-border bg-card",
  wait: "border-border bg-card",
  await: "border-amber-500/40 bg-amber-500/[0.07]",
  skill: "border-border bg-card",
  unknown: "border-dashed border-muted-foreground/50 bg-muted/30",
}

/** Icon chip tint, so the left edge of a node reads as its kind. */
const ICON_TONE: Record<Variant, string> = {
  trigger: "border-primary/30 bg-primary/10 text-primary",
  loop: "border-border bg-muted text-foreground/70",
  wait: "border-border bg-muted text-foreground/70",
  await: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  skill: "border-border bg-muted text-foreground/70",
  unknown: "border-dashed bg-muted text-muted-foreground",
}

/** Live state wins over the base tone, so progress is unmistakable. */
const STATE_TONE: Record<NonNullable<NodeData["state"]>, string> = {
  running: "border-primary bg-primary/10 ring-2 ring-primary/30",
  succeeded: "border-emerald-500/50 bg-emerald-500/[0.07]",
  failed: "border-destructive/70 bg-destructive/10",
  // Not run — an input it needed never arrived. Reads as absent, not wrong.
  skipped: "border-dashed border-muted-foreground/40 bg-muted/30 opacity-55",
}

/** Corner marker for a finished step — faster to scan than a colour alone. */
const STATE_MARK: Record<NonNullable<NodeData["state"]>, { icon: typeof Check; cls: string } | null> = {
  running: null,
  succeeded: { icon: Check, cls: "bg-emerald-500 text-white" },
  failed: { icon: X, cls: "bg-destructive text-white" },
  skipped: { icon: Minus, cls: "bg-muted-foreground/60 text-background" },
}

function StepCard({ data, selected }: NodeProps) {
  const d = data as unknown as NodeData
  const Icon =
    d.variant === "trigger"
      ? Flag
      : d.variant === "loop"
        ? Repeat
        : d.variant === "wait"
          ? Clock
          : d.variant === "await"
            ? Hourglass
            : (WORKER_ICON[d.skillId ?? ""] ?? Zap)

  const mark = d.state ? STATE_MARK[d.state] : null
  const MarkIcon = mark?.icon

  return (
    <div
      className={`group relative rounded-xl border shadow-sm transition-all ${
        d.state ? STATE_TONE[d.state] : TONE[d.variant]
      } ${selected ? "ring-2 ring-primary/40" : ""}`}
      style={{ width: NODE_W }}
    >
      {/* Running: a pulse. Finished: a small tick/cross/dash. Colour alone is
          not enough — it fails for anyone who cannot separate the hues. */}
      {d.state === "running" && (
        <span className="absolute -right-1.5 -top-1.5 flex size-3.5">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-70" />
          <span className="relative inline-flex size-3.5 rounded-full bg-primary" />
        </span>
      )}
      {MarkIcon && (
        <span
          className={`absolute -right-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full ${mark!.cls}`}
        >
          <MarkIcon className="size-2.5" strokeWidth={3} />
        </span>
      )}

      <Handle
        type="target"
        position={Position.Top}
        className="!size-1.5 !border-0 !bg-muted-foreground/50"
      />

      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <span
          className={`mt-px flex size-7 shrink-0 items-center justify-center rounded-lg border ${ICON_TONE[d.variant]}`}
        >
          <Icon className="size-3.5" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p
              className="min-w-0 flex-1 truncate text-[13px] font-medium leading-tight"
              title={d.label}
            >
              {d.label}
            </p>
            {d.progress && (
              <span className="shrink-0 rounded-full bg-primary px-1.5 py-px font-mono text-[10px] font-medium text-primary-foreground">
                {d.progress}
              </span>
            )}
          </div>

          {/* One curated line, not a dump of every input. */}
          {d.sub && (
            <p className="mt-0.5 truncate text-[11px] leading-tight text-muted-foreground" title={d.sub}>
              {d.sub}
            </p>
          )}

          {d.badge && (
            <span className="mt-1.5 inline-block rounded bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground">
              {d.badge}
            </span>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!size-1.5 !border-0 !bg-muted-foreground/50"
      />
    </div>
  )
}

const nodeTypes = { step: StepCard }

function scheduleLabel(s: Schedule): string {
  switch (s.type) {
    case "daily":
      return `Daily ${s.startTime}${s.endTime ? `–${s.endTime}` : ""} · ${s.timezone}`
    case "interval":
      return `Every ${s.everyMinutes} min`
    case "event":
      return `On ${s.event}`
    default:
      return "Manual trigger"
  }
}

/** Walks the spec into nodes + edges. Positions come from dagre afterwards. */
function toGraph(spec: WorkflowSpec): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const push = (id: string, data: NodeData) =>
    nodes.push({
      id,
      type: "step",
      position: { x: 0, y: 0 },
      data: data as unknown as Record<string, unknown>,
    })

  push("trigger", {
    label: scheduleLabel(spec.schedule),
    sub: spec.schedule?.type === "manual" ? "starts when you press Run now" : "how this workflow starts",
    variant: "trigger",
  })

  const walk = (steps: Step[], parent: string): string => {
    let prev = parent
    for (const step of steps) {
      // Widened: the union is exhaustive above, so the fallback branch
      // narrows to `never` unless we hold a loose view of the step.
      const anyStep = step as unknown as {
        kind: string
        id?: string
        label?: string
      }

      if (anyStep.kind === "loop" || anyStep.kind === "repeat") {
        const s = step as Extract<Step, { kind: "loop" | "repeat" }>
        const count = "over" in s ? s.over.length : s.times
        push(s.id, {
          label: s.label,
          badge: `${count} iterations`,
          sub: "over" in s ? s.over.join(" · ") : `repeats ${s.times} times`,
          variant: "loop",
        })
        edges.push({
          id: `${prev}->${s.id}`,
          source: prev,
          target: s.id,
          animated: true,
          type: "smoothstep",
        })
        // Children hang off the loop; the loop is the join point after.
        walk(s.steps, s.id)
        prev = s.id
        continue
      }

      if (anyStep.kind === "wait") {
        const s = step as Extract<Step, { kind: "wait" }>
        push(s.id, {
          label: s.label,
          sub: `pauses ${s.seconds < 60 ? `${s.seconds}s` : `${Math.round(s.seconds / 60)} min`}`,
          variant: "wait",
        })
      } else if (anyStep.kind === "await_event") {
        const s = step as Extract<Step, { kind: "await_event" }>
        const m = s.match ?? {}
        const what = m.reaction ? `:${m.reaction}:` : s.event.replace(/_/g, " ")
        push(s.id, {
          label: s.label,
          badge: m.messageTs ? "on that exact message" : "any match",
          sub: `waits for ${what}${m.channel ? ` in ${m.channel}` : ""}`,
          variant: "await",
        })
      } else if (anyStep.kind === "skill") {
        const s = step as Extract<Step, { kind: "skill" }>
        push(s.id, {
          label: s.label,
          skillId: s.skill,
          badge: SKILL_LABEL[s.skill] ?? s.skill,
          sub: stepDetail(s.skill, s.input),
          variant: "skill",
        })
      } else {
        // A kind the UI does not know yet still gets a node, so new step types
        // added server-side appear instead of silently disappearing.
        push(anyStep.id ?? `unknown-${nodes.length}`, {
          label: anyStep.label ?? anyStep.kind,
          badge: anyStep.kind,
          variant: "unknown",
        })
      }

      const id = anyStep.id ?? `unknown-${nodes.length - 1}`
      edges.push({ id: `${prev}->${id}`, source: prev, target: id, type: "smoothstep" })
      prev = id
    }
    return prev
  }

  walk(spec.steps ?? [], "trigger")
  return { nodes, edges }
}

/** Hierarchical layout — even ranks, centred parents, no overlap. */
function layout(nodes: Node[], edges: Edge[]): Node[] {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: "TB", nodesep: 52, ranksep: 74, marginx: 28, marginy: 28 })

  nodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H }))
  edges.forEach((e) => g.setEdge(e.source, e.target))
  dagre.layout(g)

  return nodes.map((n) => {
    const p = g.node(n.id)
    return {
      ...n,
      // dagre gives centres; React Flow wants top-left.
      position: { x: p.x - NODE_W / 2, y: p.y - NODE_H / 2 },
      targetPosition: Position.Top,
      sourcePosition: Position.Bottom,
    }
  })
}

export function WorkflowFlow({
  spec,
  height,
  stepStates,
  stepCounts,
}: {
  spec: WorkflowSpec
  /** Omit to size the canvas to the plan — a one-step plan in a 520px box is
   *  mostly empty grid, which reads as "something failed to load". */
  height?: number
  /** When present the graph highlights live progress. */
  stepStates?: StepStates
  stepCounts?: Record<string, number>
}) {
  // Layout depends only on the spec — recomputing it on every poll would
  // make the graph jump while a run is in flight.
  const base = React.useMemo(() => {
    const { nodes, edges } = toGraph(spec)
    return { nodes: layout(nodes, edges), edges }
  }, [spec])

  const initial = React.useMemo(() => {
    if (!stepStates) return base
    return {
      edges: base.edges,
      nodes: base.nodes.map((n) => {
        const state = stepStates[n.id]
        const d = n.data as unknown as NodeData
        const total =
          d.badge && /^\d+×$/.test(d.badge) ? Number(d.badge.replace("×", "")) : undefined
        const iter = stepCounts?.[n.id]
        return {
          ...n,
          data: {
            ...d,
            state,
            progress: total && iter ? `${iter} / ${total}` : undefined,
          } as unknown as Record<string, unknown>,
        }
      }),
    }
  }, [base, stepStates, stepCounts])

  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges)

  // Re-layout when the spec changes (e.g. a regenerated plan).
  React.useEffect(() => {
    setNodes(initial.nodes)
    setEdges(initial.edges)
  }, [initial, setNodes, setEdges])

  const onConnect = React.useCallback(
    (c: Connection) => setEdges((eds) => addEdge({ ...c, type: "smoothstep" }, eds)),
    [setEdges],
  )

  // A minimap earns its place only when the graph outgrows the viewport.
  const showMiniMap = nodes.length > 8
  const canvasHeight = height ?? Math.min(680, Math.max(260, nodes.length * 104 + 96))

  return (
    <div className="relative overflow-hidden rounded-lg border bg-muted/20" style={{ height: canvasHeight }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.22 }}
        minZoom={0.25}
        maxZoom={1.6}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{ type: "smoothstep" }}
        className="[&_.react-flow__edge-path]:stroke-border [&_.react-flow__edge-path]:stroke-2 [&_.react-flow__edge.animated_.react-flow__edge-path]:stroke-primary/70"
        colorMode="system"
      >
        <Background variant={BackgroundVariant.Dots} gap={18} size={1} className="opacity-40" />
        <Controls
          showInteractive={false}
          orientation="horizontal"
          className="!bottom-3 !left-3 !rounded-lg !border !border-border !bg-card/95 !shadow-sm !backdrop-blur [&>button]:!size-7 [&>button]:!border-0 [&>button]:!bg-transparent [&>button]:!text-muted-foreground [&>button:hover]:!bg-muted [&>button:hover]:!text-foreground"
        />
        {showMiniMap && (
          <MiniMap
            pannable
            zoomable
            className="!bottom-3 !right-3 !rounded-lg !border !border-border !bg-card/95 !shadow-sm"
            maskColor="var(--color-muted)"
            nodeColor={(n) => {
              const d = n.data as unknown as NodeData
              if (d?.state === "failed") return "var(--color-destructive)"
              if (d?.state === "running") return "var(--color-primary)"
              return "var(--color-muted-foreground)"
            }}
          />
        )}
      </ReactFlow>

      {/* Legend, only while a run is being watched — otherwise it is noise. */}
      {stepStates && (
        <div className="pointer-events-none absolute right-3 top-3 flex flex-wrap items-center gap-3 rounded-lg border bg-card/95 px-2.5 py-1.5 text-[11px] shadow-sm backdrop-blur">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-primary" /> running
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-emerald-500" /> done
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-destructive" /> failed
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-muted-foreground/50" /> skipped
          </span>
        </div>
      )}
    </div>
  )
}
